import cn from 'classnames';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { useSelector } from 'react-redux';

import { SalesByTopChannelsGraph } from '../../graphs/SalesByTopChannelsGraph';
import { AnalyticsItemBox } from './AnalyticsItemBox';

import { AnalyticsOrderType } from '../../../constants/analytics';
import { getSalesByTopChannelsGraphTitle, groupByDateAndRetailerId } from '../../tables/reports/utils';
import { prepareDataForSalesGraph, shouldShowYearsInXAxis, sortRetailerIdsByTotals } from '../../../utils/analytics';
import { MAX_DISPLAYED_RETAILERS } from '../../graphs/consts';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import { REPORT_TYPES } from '../../tables/reports/consts';

import styles from '../../graphs/graph.module.scss';

export function SalesByTopChannelsGraphContainer() {
  const {
    filters,
    currentOrderType,
    currentCurrency,
    retailerNamesByIds,
    metricsDataType: { 'sales-by-top-channels': currentMetric },
    ui: {
      loading: { [REPORT_TYPES.SALES_BY_DAY]: isLoading },
    },
    additionalData: {
      [REPORT_TYPES.SALES_BY_DAY]: { data },
    },
  } = useSelector((state) => state.analytics);
  const mixpanel = useMixpanel();

  useEffect(() => {
    reportBusinessActionToMixpanel(mixpanel, 'sales-dashboard-sales-by-top-channels-over-time-chart', {
      chosenValue: currentMetric,
    });
  }, [currentMetric, mixpanel]);

  const isData = !!data && Array.isArray(data);
  const isGmvMetric = currentOrderType === AnalyticsOrderType.SALES;

  const totalsByRetailerId = (data || []).reduce((acc, item) => {
    acc[item.retailer_id] = {
      gmv: 0,
      units: 0,
      orders: 0,
    };
    acc[item.retailer_id].gmv += item.gmv[currentCurrency].all_orders;
    acc[item.retailer_id].units += item.unit_count.all_orders;
    acc[item.retailer_id].orders += item.total_orders_count;
    return acc;
  }, {});

  const sortedRetailerIdsByTotals = sortRetailerIdsByTotals({ totalsByRetailerId, isGmvMetric, currentOrderType });

  const groupedByDate = useMemo(
    () =>
      isData
        ? groupByDateAndRetailerId({ data, showYears: shouldShowYearsInXAxis(filters.dates.start, filters.dates.end) })
        : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isData, data, currentMetric],
  );

  const graphData = isEmpty(groupedByDate)
    ? []
    : prepareDataForSalesGraph({
        dates: filters.dates,
        groupedByDate,
        retailerNamesByIds,
        currency: currentCurrency,
      });

  const topRetailerIds = sortedRetailerIdsByTotals
    .map(([retailerId]) => Number(retailerId))
    .slice(0, MAX_DISPLAYED_RETAILERS);
  const title = useMemo(() => getSalesByTopChannelsGraphTitle(topRetailerIds), [topRetailerIds]);

  const filteredGmvGraphData = graphData.gmv?.filter((item) => topRetailerIds.includes(Number(item.salesChannelId)));
  const filteredUnitsGraphData = graphData.units?.filter((item) =>
    topRetailerIds.includes(Number(item.salesChannelId)),
  );
  const filteredOrdersGraphData = graphData.orders?.filter((item) =>
    topRetailerIds.includes(Number(item.salesChannelId)),
  );
  filteredGmvGraphData?.sort(
    (datumA, datumB) => topRetailerIds.indexOf(datumB.salesChannelId) - topRetailerIds.indexOf(datumA.salesChannelId),
  );
  filteredUnitsGraphData?.sort(
    (datumA, datumB) => topRetailerIds.indexOf(datumB.salesChannelId) - topRetailerIds.indexOf(datumA.salesChannelId),
  );
  filteredOrdersGraphData?.sort(
    (datumA, datumB) => topRetailerIds.indexOf(datumB.salesChannelId) - topRetailerIds.indexOf(datumA.salesChannelId),
  );

  if (isLoading || !data) {
    return (
      <AnalyticsItemBox title={title} showLoader={true}>
        <div className={cn(styles.graphWrapper, styles.stackedLineGraph)} />
      </AnalyticsItemBox>
    );
  }

  return (
    <SalesByTopChannelsGraph
      currentCurrency={currentCurrency}
      currentOrderType={currentOrderType}
      title={title}
      data={{
        gmv: filteredGmvGraphData?.reverse() || [],
        units: filteredUnitsGraphData?.reverse() || [],
        orders: filteredOrdersGraphData?.reverse() || [],
      }}
      metric="sales-by-top-channels"
      isGmvMetric={isGmvMetric}
    />
  );
}
